import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { WidgetConfig, ChartOptions, ColorPalette } from '@/types/widget';
import type { DateBucket } from '@/types/query';
import {
  aggregationLabelKey,
  DEFAULT_BREAKDOWN_LIMIT,
  DEFAULT_DATE_BUCKET,
  buildSpec,
} from '@/engine/widget-spec';
import { buildSql, formatSqlForDisplay } from '@/engine/build-sql';
import { useDatasetStore } from '@/store/dataset-store';
import { useFiltersStore } from '@/store/filters-store';
import { isDateType } from '@/features/filters/column-types';

type WidgetSettingsFormProps = {
  widget: WidgetConfig;
  onSave: (patch: Partial<WidgetConfig>) => void;
  onClose: () => void;
};

/**
 * Option lists carry their underlying value only — display labels
 * resolve through i18n at render time so the modal repaints on
 * locale switch. Keep these in source order for the dropdowns.
 */
const PIE_LABEL_OPTION_VALUES = [
  'both',
  'name-value',
  'name-percent',
  'percent-value',
  'percent',
  'value',
  'name',
] as const;

const PIE_POSITION_OPTION_VALUES = ['outside', 'inside'] as const;

const PALETTE_OPTION_VALUES: ColorPalette[] = [
  'default',
  'vibrant',
  'pastel',
  'cool',
  'warm',
  'mono',
];

const DATE_BUCKET_OPTION_VALUES: DateBucket[] = ['day', 'month', 'year'];

/**
 * Inline settings form for an existing widget — rendered inside the
 * right panel (Phase 11 batch 4). Edits title, chart display options,
 * note, and row limit; submit applies via `onSave`, then `onClose`.
 */
export function WidgetSettingsForm({
  widget,
  onSave,
  onClose,
}: WidgetSettingsFormProps): JSX.Element {
  const { t } = useTranslation();
  const [showSql, setShowSql] = useState(false);
  const [title, setTitle] = useState(widget.title);
  const [limit, setLimit] = useState(widget.limit ?? 10);
  const [note, setNote] = useState(widget.note ?? '');
  const [opts, setOpts] = useState<ChartOptions>(widget.chartOptions ?? {});
  const [mainLabel, setMainLabel] = useState(widget.metric.label ?? '');
  const [extraLabels, setExtraLabels] = useState<string[]>(
    (widget.extraMetrics ?? []).map((m) => m.label ?? ''),
  );
  const [dateBucket, setDateBucket] = useState<DateBucket>(
    widget.dateBucket ?? DEFAULT_DATE_BUCKET,
  );
  const [breakdownBy, setBreakdownBy] = useState<string>(
    widget.breakdownBy ?? '',
  );
  const [breakdownLimit, setBreakdownLimit] = useState<number>(
    widget.breakdownLimit ?? DEFAULT_BREAKDOWN_LIMIT,
  );

  // Look up groupBy column type to decide whether the date-bucket
  // selector applies. Only DATE/TIMESTAMP columns get bucketing.
  const groupBySqlType = useDatasetStore((s) => {
    if (!widget.groupBy) return undefined;
    const cols = s.datasets.get(widget.datasetId)?.columns;
    return cols?.find((c) => c.name === widget.groupBy)?.sqlType;
  });
  const isTimeAxis = !!groupBySqlType && isDateType(groupBySqlType);
  // Dataset columns drive the Seri kırılımı dropdown. Filter out the
  // current groupBy column (it's already the X axis) and any column
  // already used as an extra metric to keep options meaningful.
  const allColumns = useDatasetStore(
    (s) => s.datasets.get(widget.datasetId)?.columns ?? [],
  );
  const breakdownColumns = allColumns.filter((c) => c.name !== widget.groupBy);

  // Live SQL preview — what the worker would actually run for this
  // widget under the current global filters + value groupings. Recomputes
  // on every render; cheap (string ops only).
  const filters = useFiltersStore((s) => s.filters);
  const valueGroupings = useDatasetStore(
    (s) => s.datasets.get(widget.datasetId)?.valueGroupings,
  );
  const previewSql = formatSqlForDisplay(
    buildSql(buildSpec(widget, filters, groupBySqlType, valueGroupings)),
  );

  const isChart = widget.kind !== 'kpi';
  const isPie = widget.kind === 'pie';
  const isLine = widget.kind === 'line';
  const isBar = widget.kind === 'bar';
  const isCompareTable = widget.kind === 'compare-table';
  // Compare-table caps "Top N" at 10: each winners/losers side is
  // already short by design, larger top-Ns produce a wall-of-numbers
  // that defeats the highlight intent (item: satır limiti 10 olsun).
  const limitMax = isCompareTable ? 10 : 500;

  function handleSave(): void {
    const patch: Partial<WidgetConfig> = { title };
    if (isChart) {
      patch.limit = limit;
      patch.chartOptions = opts;
      if (isTimeAxis) patch.dateBucket = dateBucket;
    }
    const trimmedMain = mainLabel.trim();
    patch.metric = {
      column: widget.metric.column,
      aggregation: widget.metric.aggregation,
      ...(trimmedMain ? { label: trimmedMain } : {}),
    };
    if (widget.extraMetrics && widget.extraMetrics.length > 0) {
      patch.extraMetrics = widget.extraMetrics.map((em, i) => {
        const trimmed = (extraLabels[i] ?? '').trim();
        return {
          column: em.column,
          aggregation: em.aggregation,
          ...(trimmed ? { label: trimmed } : {}),
        };
      });
    }
    // Persist the breakdown selection. Setting undefined clears the
    // fields so the widgets store's shallow merge can drop them — see
    // WidgetConfig.breakdownBy where the `| undefined` is explicit
    // (exactOptionalPropertyTypes is on). Compare-mode widgets always
    // clear the fields (the UI hides the editor in compare mode; this
    // catches any stale state inherited from a legacy save).
    if (breakdownBy && widget.mode !== 'compare') {
      patch.breakdownBy = breakdownBy;
      patch.breakdownLimit = breakdownLimit;
    } else {
      patch.breakdownBy = undefined;
      patch.breakdownLimit = undefined;
    }
    patch.note = note.trim() || undefined;
    onSave(patch);
    onClose();
  }

  function updateOpt<K extends keyof ChartOptions>(key: K, val: ChartOptions[K]): void {
    setOpts((prev) => ({ ...prev, [key]: val }));
  }

  return (
    <div className="flex flex-col gap-3" style={{ color: 'var(--color-fg)' }}>
        {/* Title */}
        <Field label={t('widgetSettings.titleField')}>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full text-xs p-1.5 rounded"
            style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg)' }}
          />
        </Field>

        {/* Row limit — charts only. `limit === 0` is the unlimited
            sentinel; the checkbox flips that without losing the previous
            numeric value, matching the QueryBuilder Add modal. */}
        {isChart && (
          <Field label={t('widgetSettings.limit')}>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={limitMax}
                value={limit === 0 ? '' : limit}
                disabled={limit === 0}
                onChange={(e) =>
                  setLimit(
                    Math.max(1, Math.min(limitMax, Number(e.target.value) || 1)),
                  )
                }
                className="flex-1 text-xs p-1.5 rounded disabled:opacity-50"
                style={{
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-bg)',
                }}
              />
              <label
                className="flex items-center gap-1 text-xs cursor-pointer shrink-0"
                style={{ color: 'var(--color-muted)' }}
              >
                <input
                  type="checkbox"
                  checked={limit === 0}
                  onChange={(e) => setLimit(e.target.checked ? 0 : 10)}
                />
                {t('widgetSettings.unlimited')}
              </label>
            </div>
          </Field>
        )}

        {/* Chart display options */}
        {isChart && (
          <>
            <div
              className="border-t pt-2 mt-1"
              style={{ borderColor: 'var(--color-border)' }}
            >
              <h3
                className="text-xs font-semibold mb-2"
                style={{ color: 'var(--color-muted)' }}
              >
                {t('widgetSettings.chartOptions')}
              </h3>
            </div>

            {(isBar || isLine) && (
              <>
                <Toggle
                  label={t('widgetSettings.showXAxis')}
                  checked={opts.showXAxisLabels !== false}
                  onChange={(v) => updateOpt('showXAxisLabels', v)}
                />

                <Toggle
                  label={t('widgetSettings.showYAxis')}
                  checked={opts.showYAxisLabels !== false}
                  onChange={(v) => updateOpt('showYAxisLabels', v)}
                />
              </>
            )}

            <Toggle
              label={t('widgetSettings.showLegend')}
              checked={opts.showLegend !== false}
              onChange={(v) => updateOpt('showLegend', v)}
            />

            <Field label={t('widgetSettings.colorPalette')}>
              <select
                value={opts.colorPalette ?? 'default'}
                onChange={(e) =>
                  updateOpt('colorPalette', e.target.value as ColorPalette)
                }
                className="w-full text-xs p-1.5 rounded"
                style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg)' }}
              >
                {PALETTE_OPTION_VALUES.map((v) => (
                  <option key={v} value={v}>
                    {t(`widgetSettings.palette.${v}`)}
                  </option>
                ))}
              </select>
            </Field>

            {(isBar || isLine) && (
              <Field label={t('widgetSettings.axisTitleFontSize')}>
                <input
                  type="number"
                  min={8}
                  max={24}
                  value={opts.axisTitleFontSize ?? 11}
                  onChange={(e) =>
                    updateOpt(
                      'axisTitleFontSize',
                      Math.max(8, Math.min(24, Number(e.target.value) || 11)),
                    )
                  }
                  className="w-full text-xs p-1.5 rounded"
                  style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg)' }}
                />
                <span className="text-[10px]" style={{ color: 'var(--color-muted)' }}>
                  {t('widgetSettings.axisTitleFontSizeHint')}
                </span>
              </Field>
            )}

            {isTimeAxis && !isPie && (
              <Field label={t('widgetSettings.dateBucket')}>
                <select
                  value={dateBucket}
                  onChange={(e) => setDateBucket(e.target.value as DateBucket)}
                  className="w-full text-xs p-1.5 rounded"
                  style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg)' }}
                >
                  {DATE_BUCKET_OPTION_VALUES.map((v) => (
                    <option key={v} value={v}>
                      {t(`widgetSettings.dateBucketOptions.${v}`)}
                    </option>
                  ))}
                </select>
                <span className="text-[10px]" style={{ color: 'var(--color-muted)' }}>
                  {t('widgetSettings.dateBucketHint')}
                </span>
              </Field>
            )}

            {(isBar || isLine) && (
              <Field label={t('widgetSettings.xAxisRotation')}>
                <input
                  type="number"
                  min={0}
                  max={90}
                  value={opts.xAxisRotation ?? 0}
                  onChange={(e) => updateOpt('xAxisRotation', Number(e.target.value) || 0)}
                  className="w-full text-xs p-1.5 rounded"
                  style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg)' }}
                />
                <span className="text-[10px]" style={{ color: 'var(--color-muted)' }}>
                  {t('widgetSettings.xAxisRotationHint')}
                </span>
              </Field>
            )}

            {(isBar || isLine) && (
              <>
                <Field label={t('widgetSettings.xAxisTitle')}>
                  <input
                    type="text"
                    value={opts.xAxisTitle ?? ''}
                    onChange={(e) => updateOpt('xAxisTitle', e.target.value)}
                    placeholder={t('widgetSettings.xAxisTitlePlaceholder', {
                      example: widget.groupBy ?? t('widgetSettings.categoryPlaceholder'),
                    })}
                    className="w-full text-xs p-1.5 rounded"
                    style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg)' }}
                  />
                </Field>
                <Field label={t('widgetSettings.yAxisTitle')}>
                  <input
                    type="text"
                    value={opts.yAxisTitle ?? ''}
                    onChange={(e) => updateOpt('yAxisTitle', e.target.value)}
                    placeholder={t('widgetSettings.yAxisTitlePlaceholder', {
                      example: `${t(aggregationLabelKey(widget.metric.aggregation))} ${widget.metric.column}`,
                    })}
                    className="w-full text-xs p-1.5 rounded"
                    style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg)' }}
                  />
                </Field>
              </>
            )}

            {isLine && (
              <Toggle
                label={t('widgetSettings.smooth')}
                checked={opts.smooth !== false}
                onChange={(v) => updateOpt('smooth', v)}
              />
            )}

            {/* Seri kırılımı — bar / line only. Hidden when the widget
                has extra metrics configured (mutual exclusion) and when
                the widget belongs to compare mode (the __period__
                dimension already owns the series axis). */}
            {(isBar || isLine) &&
              widget.mode !== 'compare' &&
              (widget.extraMetrics?.length ?? 0) === 0 && (
              <div
                className="border-t pt-2 mt-1 flex flex-col gap-2"
                style={{ borderColor: 'var(--color-border)' }}
              >
                <div className="flex items-center justify-between">
                  <h3
                    className="text-xs font-semibold"
                    style={{ color: 'var(--color-muted)' }}
                  >
                    {t('queryBuilder.breakdown.title')}
                  </h3>
                  {breakdownBy ? (
                    <button
                      type="button"
                      onClick={() => setBreakdownBy('')}
                      className="text-xs px-2 py-0.5 rounded hover:opacity-80"
                      style={{ color: 'var(--color-danger)' }}
                    >
                      {t('queryBuilder.breakdown.disable')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        const firstCol = breakdownColumns[0]?.name ?? '';
                        if (firstCol) setBreakdownBy(firstCol);
                      }}
                      disabled={breakdownColumns.length === 0}
                      className="text-xs px-2 py-0.5 rounded hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
                      style={{
                        color: 'var(--color-accent)',
                        border: '1px dashed var(--color-accent)',
                      }}
                    >
                      {t('queryBuilder.breakdown.enable')}
                    </button>
                  )}
                </div>

                {!breakdownBy && (
                  <span
                    className="text-[11px]"
                    style={{ color: 'var(--color-muted)' }}
                  >
                    {t('queryBuilder.breakdown.hint')}
                  </span>
                )}

                {breakdownBy && (
                  <>
                    <Field label={t('queryBuilder.breakdown.column')}>
                      <select
                        value={breakdownBy}
                        onChange={(e) => setBreakdownBy(e.target.value)}
                        className="w-full text-xs p-1.5 rounded"
                        style={{
                          border: '1px solid var(--color-border)',
                          background: 'var(--color-bg)',
                        }}
                      >
                        {breakdownColumns.map((c) => (
                          <option key={c.name} value={c.name}>
                            {c.name} ({c.sqlType})
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label={t('queryBuilder.breakdown.limit')}>
                      <input
                        type="number"
                        min={1}
                        max={50}
                        value={breakdownLimit}
                        onChange={(e) =>
                          setBreakdownLimit(
                            Math.max(1, Math.min(50, Number(e.target.value) || 1)),
                          )
                        }
                        className="w-full text-xs p-1.5 rounded"
                        style={{
                          border: '1px solid var(--color-border)',
                          background: 'var(--color-bg)',
                        }}
                      />
                    </Field>
                  </>
                )}
              </div>
            )}

            {isBar && (
              <Toggle
                label={t('widgetSettings.showBarLabels')}
                checked={opts.showBarLabels === true}
                onChange={(v) => updateOpt('showBarLabels', v)}
              />
            )}

            {isPie && (
              <>
                <Field label={t('widgetSettings.pieLabelFormat')}>
                  <select
                    value={opts.pieLabelFormat ?? 'both'}
                    onChange={(e) =>
                      updateOpt('pieLabelFormat', e.target.value as ChartOptions['pieLabelFormat'])
                    }
                    className="w-full text-xs p-1.5 rounded"
                    style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg)' }}
                  >
                    {PIE_LABEL_OPTION_VALUES.map((v) => (
                      <option key={v} value={v}>
                        {t(`widgetSettings.pieLabelFormatOptions.${v}`)}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label={t('widgetSettings.pieLabelPosition')}>
                  <select
                    value={opts.pieLabelPosition ?? 'outside'}
                    onChange={(e) =>
                      updateOpt('pieLabelPosition', e.target.value as ChartOptions['pieLabelPosition'])
                    }
                    className="w-full text-xs p-1.5 rounded"
                    style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg)' }}
                  >
                    {PIE_POSITION_OPTION_VALUES.map((v) => (
                      <option key={v} value={v}>
                        {t(`widgetSettings.pieLabelPositionOptions.${v}`)}
                      </option>
                    ))}
                  </select>
                </Field>

                <Toggle
                  label={t('widgetSettings.donut')}
                  checked={opts.donut !== false}
                  onChange={(v) => updateOpt('donut', v)}
                />

                <Toggle
                  label={t('widgetSettings.pieShowTotal')}
                  checked={opts.pieShowTotal === true}
                  onChange={(v) => updateOpt('pieShowTotal', v)}
                />
              </>
            )}
          </>
        )}

        {/* Series labels */}
        <div
          className="border-t pt-2 mt-1 flex flex-col gap-2"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <h3
            className="text-xs font-semibold"
            style={{ color: 'var(--color-muted)' }}
          >
            {t('widgetSettings.seriesLabels')}
          </h3>
          <Field
            label={`${t(aggregationLabelKey(widget.metric.aggregation))} ${widget.metric.column}`}
          >
            <input
              type="text"
              value={mainLabel}
              onChange={(e) => setMainLabel(e.target.value)}
              placeholder={t('widgetSettings.customLabelPlaceholder')}
              className="w-full text-xs p-1.5 rounded"
              style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg)' }}
            />
          </Field>
          {widget.extraMetrics?.map((em, i) => (
            <Field
              key={`${em.column}-${em.aggregation}-${i}`}
              label={`${t(aggregationLabelKey(em.aggregation))} ${em.column}`}
            >
              <input
                type="text"
                value={extraLabels[i] ?? ''}
                onChange={(e) => {
                  const next = [...extraLabels];
                  next[i] = e.target.value;
                  setExtraLabels(next);
                }}
                placeholder={t('widgetSettings.customLabelPlaceholder')}
                className="w-full text-xs p-1.5 rounded"
                style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg)' }}
              />
            </Field>
          ))}
        </div>

        {/* Note */}
        <div
          className="border-t pt-2 mt-1"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <Field label={t('widgetSettings.note')}>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder={t('widgetSettings.notePlaceholder')}
              className="w-full text-xs p-1.5 rounded resize-y"
              style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg)', minHeight: '4.5rem' }}
            />
          </Field>
        </div>

        {/* Show SQL toggle — power-user disclosure (Phase 6 carryover). */}
        <div
          className="border-t pt-2 mt-1"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <button
            type="button"
            onClick={() => setShowSql((v) => !v)}
            aria-expanded={showSql}
            className="text-[11px] uppercase tracking-wide font-semibold flex items-center gap-1.5"
            style={{ color: 'var(--color-muted)' }}
          >
            <span>{showSql ? '▾' : '▸'}</span>
            <span>{t('widgetSettings.showSql')}</span>
          </button>
          {showSql && (
            <div className="mt-2 flex flex-col gap-1.5">
              <pre
                className="text-[11px] p-2 rounded font-mono whitespace-pre-wrap break-all max-h-40 overflow-y-auto"
                style={{
                  background: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  color: 'var(--color-fg)',
                }}
              >
                {previewSql}
              </pre>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard?.writeText(previewSql);
                }}
                className="self-end text-[11px] underline"
                style={{ color: 'var(--color-muted)' }}
              >
                {t('widgetSettings.copy')}
              </button>
            </div>
          )}
        </div>

        <div className="flex gap-2 justify-end pt-1">
          <button
            type="button"
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded"
            style={{ color: 'var(--color-muted)' }}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="text-xs px-3 py-1.5 rounded text-white"
            style={{ background: 'var(--color-accent)' }}
          >
            {t('common.save')}
          </button>
        </div>
    </div>
  );
}

/**
 * Right-side panel wrapper that hosts {@link WidgetSettingsForm}
 * (Phase 11 batch 4). Sits beside the main canvas at 248px width;
 * mount/unmount is driven by `useUiStore.widgetSettingsTarget`.
 */
export function WidgetSettingsRightPanel({
  widget,
  onSave,
  onClose,
}: WidgetSettingsFormProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <aside
      className="shrink-0 flex flex-col overflow-y-auto"
      style={{
        width: 'var(--right-panel-width)',
        background: 'var(--color-surface)',
        borderLeft: '1px solid var(--color-border)',
      }}
      aria-label={t('widgetSettings.title')}
    >
      <header
        className="flex items-start justify-between gap-2 px-3.5 py-2.5 border-b shrink-0"
        style={{ borderColor: 'var(--color-border)' }}
      >
        <div className="min-w-0 flex-1">
          <div
            className="text-[11px] font-bold uppercase"
            style={{ color: 'var(--color-muted)', letterSpacing: '0.07em' }}
          >
            {t('widgetSettings.title')}
          </div>
          <div
            className="text-[10px] truncate"
            style={{ color: 'var(--color-muted)' }}
            title={widget.title}
          >
            {widget.title}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close')}
          className="flex items-center justify-center p-0.5 border-0 bg-transparent cursor-pointer hover:opacity-80 shrink-0"
          style={{ color: 'var(--color-muted)' }}
        >
          <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden>
            <path
              d="M2 2L12 12M12 2L2 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>
      <div className="flex-1 p-3.5">
        <WidgetSettingsForm widget={widget} onSave={onSave} onClose={onClose} />
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <label className="text-xs" style={{ color: 'var(--color-muted)' }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}): JSX.Element {
  return (
    <label
      className="flex items-center gap-2 text-xs cursor-pointer"
      style={{ color: 'var(--color-fg)' }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}
